import useSWR, { mutate } from "swr";
import type {
	IOfficeHours,
	ISlaConfig,
	ISlaMetrics,
	ISpamReport,
	ISpamSettings,
	ISupportMacro,
	ISupportTicket,
	ITicketAutoCloseSettings,
	ITicketCategory,
	ITicketDashboardStats,
	ITicketMessage,
	TicketReplyState,
} from "types/support-ticket.types";

/** SWR keys, exported so writers revalidate the exact entry readers use. */
export const SUPPORT_MACROS_KEY = "admin/support-tickets/macros";
export const OFFICE_HOURS_KEY = "admin/support-tickets/office-hours";
export const SPAM_SETTINGS_KEY = "admin/support-tickets/spam-settings";
export const SPAM_REPORT_KEY = "admin/support-tickets/spam-settings/report";

export interface TicketListResponse {
	data: ISupportTicket[];
	total: number;
	/**
	 * Quarantined tickets matching the same `search`. Sent only when a search
	 * runs outside the Suspected spam tab, so a search never hides them silently.
	 */
	suspectedSpamMatches?: number;
}

export interface TicketListParams {
	status?: string | string[];
	priority?: string | string[];
	source?: string;
	assignedToUserId?: number;
	assignedToId?: number;
	categoryId?: number;
	companyId?: number;
	search?: string;
	slaBreached?: boolean;
	replyState?: TicketReplyState;
	limit?: number;
	offset?: number;
	orderBy?: string;
	orderDir?: "ASC" | "DESC";
}

function buildQueryString(params: Record<string, any>): string {
	const filtered = Object.entries(params).filter(
		([_, v]) => v !== undefined && v !== null && v !== "",
	);
	if (filtered.length === 0) return "";
	return (
		"?" +
		new URLSearchParams(filtered.map(([k, v]) => [k, String(v)])).toString()
	);
}

export function useSupportTickets(params: TicketListParams = {}) {
	const qs = buildQueryString(params);
	return useSWR<TicketListResponse>(`admin/support-tickets${qs}`, {
		revalidateOnFocus: false,
	});
}

export function useSupportTicket(id: number | null) {
	return useSWR<ISupportTicket>(id ? `admin/support-tickets/${id}` : null, {
		revalidateOnFocus: false,
	});
}

export function useTicketMessages(ticketId: number | null) {
	return useSWR<ITicketMessage[]>(
		ticketId ? `admin/support-tickets/${ticketId}/messages` : null,
		{
			revalidateOnFocus: false,
		},
	);
}

export function useTicketDashboard(enabled = true) {
	return useSWR<ITicketDashboardStats>(
		enabled ? "admin/support-tickets/dashboard/stats" : null,
		{
			revalidateOnFocus: false,
			refreshInterval: 30000,
		},
	);
}

/**
 * Count of tickets actually waiting on support: still active, and the newest
 * customer-visible message is not one of ours. Falls to zero as replies go out
 * and climbs again when a customer writes back.
 *
 * Shares the SWR key with the support-tickets dashboard, so having both mounted
 * costs one request.
 */
export function useTicketsAwaitingReply(enabled = true) {
	const { data } = useTicketDashboard(enabled);
	return data?.awaitingReply || 0;
}

export function useSlaMetrics() {
	return useSWR<ISlaMetrics>("admin/support-tickets/dashboard/sla", {
		revalidateOnFocus: false,
		refreshInterval: 30000,
	});
}

export function useTicketCategories() {
	return useSWR<ITicketCategory[]>("admin/support-tickets/categories", {
		revalidateOnFocus: false,
	});
}

export function useSlaConfigs() {
	return useSWR<ISlaConfig[]>("admin/support-tickets/sla-config", {
		revalidateOnFocus: false,
	});
}

export function useAutoCloseSettings() {
	return useSWR<ITicketAutoCloseSettings>(
		"admin/support-tickets/auto-close-settings",
		{ revalidateOnFocus: false },
	);
}

export function useSupportMacros() {
	return useSWR<ISupportMacro[]>(SUPPORT_MACROS_KEY, {
		revalidateOnFocus: false,
	});
}

/** Mode and switches. Pass false to skip the request until it is needed. */
export function useSpamSettings(enabled = true) {
	return useSWR<ISpamSettings>(enabled ? SPAM_SETTINGS_KEY : null, {
		revalidateOnFocus: false,
	});
}

/** 30 days of verdict counts and the tickets under the two spam verdicts. */
export function useSpamReport() {
	return useSWR<ISpamReport>(SPAM_REPORT_KEY, { revalidateOnFocus: false });
}

export function useOfficeHours() {
	return useSWR<IOfficeHours>(OFFICE_HOURS_KEY, { revalidateOnFocus: false });
}

export function useMyTicketCategories() {
	return useSWR<ITicketCategory[]>("user/support-tickets/categories", {
		revalidateOnFocus: false,
	});
}

export function useMyTickets() {
	return useSWR<{ data: ISupportTicket[]; total: number }>(
		"user/support-tickets",
		{
			revalidateOnFocus: false,
		},
	);
}

/**
 * Customer-side mirror of {@link useTicketsAwaitingReply}: how many of my own
 * live tickets support has replied to and I have not answered.
 *
 * "Unanswered", not "unread" — there is no per-message read state, so opening
 * the ticket does not clear this. It drops when the user replies or the ticket
 * is resolved/closed.
 */
export function useMyTicketsAwaitingReply(enabled = true) {
	const { data } = useSWR<{ awaitingReply: number }>(
		enabled ? "user/support-tickets/awaiting-reply" : null,
		{
			revalidateOnFocus: false,
			refreshInterval: 60000,
		},
	);

	return data?.awaitingReply || 0;
}

export function useMyTicket(id: number | null) {
	return useSWR<ISupportTicket>(id ? `user/support-tickets/${id}` : null, {
		revalidateOnFocus: false,
	});
}

export function useMyTicketMessages(ticketId: number | null) {
	return useSWR<ITicketMessage[]>(
		ticketId ? `user/support-tickets/${ticketId}/messages` : null,
		{
			revalidateOnFocus: false,
		},
	);
}

// Cache invalidation helpers
export const invalidateTickets = () =>
	mutate(
		(key) => typeof key === "string" && key.startsWith("admin/support-tickets"),
		undefined,
		{ revalidate: true },
	);
export const invalidateTicket = (id: number) =>
	mutate(`admin/support-tickets/${id}`);
export const invalidateTicketMessages = (id: number) =>
	mutate(`admin/support-tickets/${id}/messages`);
export const invalidateMyTickets = () =>
	mutate(
		(key) => typeof key === "string" && key.startsWith("user/support-tickets"),
		undefined,
		{ revalidate: true },
	);
